import React from "react";
import { Trophy, Facebook, MapPin, Calendar, Clock } from "lucide-react";
import EventRegistrationForm from "../components/EventRegistrationForm";
import usePageMeta from "../hooks/usePageMeta";

const LOGO = "/small-town-showdown-logo.png";
const FB_URL = "https://www.facebook.com/share/1C3AhXRure/?mibextid=wwXIfr";

export default function SmallTownShowdown() {
  usePageMeta({
    title: "Small Town Showdown BBQ Competition · March 11-14, 2027",
    description:
      "Small Town Showdown BBQ Competition — March 11th through 14th, 2027 in Shady Hills, FL. Register your team for KCBS Professional, Backyard, Kids Que, and specialty categories. Live pit, big trophies, small town heart.",
    path: "/small-town-showdown",
    image: LOGO,
  });

  return (
    <div className="bg-bone">
      {/* HERO — logo + event info */}
      <section className="border-b border-ink py-16 md:py-20" data-testid="sts-hero">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 px-4 md:grid-cols-12 md:px-8">
          <div className="md:col-span-5">
            <div className="mx-auto max-w-md md:mx-0">
              <img
                src={LOGO}
                alt="Small Town Showdown BBQ Competition"
                className="w-full drop-shadow-xl"
                data-testid="sts-logo"
              />
            </div>
          </div>
          <div className="md:col-span-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink/60">BBQ Competition · March 2027</p>
            <h1 className="mt-3 font-rye text-5xl uppercase leading-[0.9] sm:text-6xl lg:text-7xl">
              Small Town Showdown
            </h1>
            <p className="mt-5 max-w-xl font-serif text-base leading-relaxed text-ink/80 md:text-lg">
              Small town heart. Big pit energy. Bring your rig, your rub, and your best game to
              Shady Hills for two days of KCBS-sanctioned Professional, backyard bragging rights,
              and Kids Que memories.
            </p>

            <ul className="mt-6 space-y-3 font-serif text-sm text-ink/85">
              <li className="flex items-start gap-3">
                <Calendar className="mt-0.5 shrink-0" size={18} />
                <span><b>March 11th–14th, 2027</b></span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="mt-0.5 shrink-0" size={18} />
                <span>Load-in Thursday from <b>1:00 PM</b></span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 shrink-0" size={18} />
                <span><b>15362 Alric Pottberg Rd, Shady Hills, FL 34610</b></span>
              </li>
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#sts-register"
                data-testid="sts-cta-register"
                className="border-2 border-ink bg-ink px-6 py-3 font-alfa text-base uppercase tracking-[0.2em] text-bone transition-colors hover:bg-transparent hover:text-ink"
              >
                Register Your Team
              </a>
              <a
                href={FB_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sts-cta-facebook"
                className="inline-flex items-center gap-2 border-2 border-ink px-6 py-3 font-alfa text-base uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ink hover:text-bone"
              >
                <Facebook size={18} /> Follow on Facebook
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Last Year's Champions */}
      <section className="border-b border-ink bg-ink py-14 md:py-20" data-testid="sts-champions">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-bone/60">Last year's champions</p>
          <h2 className="mt-3 font-alfa text-3xl uppercase text-bone md:text-4xl">The Titles to Chase</h2>

          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="border-2 border-bone/70 bg-transparent p-6 md:p-8" data-testid="sts-champ-pro">
              <div className="flex items-start gap-4">
                <Trophy size={32} className="mt-1 shrink-0 text-yellow-400" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-bone/60">Professional Grand Champion</p>
                  <p className="mt-2 font-alfa text-3xl uppercase leading-tight text-bone">Ryan King</p>
                  <p className="mt-2 font-serif text-sm text-bone/80">
                    KCBS Professional 4-Meat winner. The bar is set — think you can top it?
                  </p>
                </div>
              </div>
            </div>

            <div className="border-2 border-bone/70 bg-transparent p-6 md:p-8" data-testid="sts-champ-backyard">
              <div className="flex items-start gap-4">
                <Trophy size={32} className="mt-1 shrink-0 text-yellow-400" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-bone/60">Backyard Grand Champion</p>
                  <p className="mt-2 font-alfa text-3xl uppercase leading-tight text-bone">Big and Ugly</p>
                  <p className="mt-2 font-serif text-sm text-bone/80">
                    KCBS Backyard 4-Meat winner. The bar is set — think you can top it?
                  </p>
                </div>
              </div>
            </div>

            <div className="border-2 border-bone/70 bg-transparent p-6 md:p-8" data-testid="sts-champ-kids">
              <div className="flex items-start gap-4">
                <Trophy size={32} className="mt-1 shrink-0 text-yellow-400" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-bone/60">Kids Que Grand Champion</p>
                  <p className="mt-2 font-alfa text-3xl uppercase leading-tight text-bone">Berkley Montano</p>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-bone/70">When Pigs Fly Family BBQ</p>
                  <p className="mt-2 font-serif text-sm text-bone/80">
                    Kids Que reigning champ. The bar is set — think you can top it?
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section id="sts-register" className="border-b border-ink py-16 md:py-20" data-testid="sts-registration-section">
        <div className="mx-auto max-w-[1000px] px-4 md:px-8">
          <EventRegistrationForm />
        </div>
      </section>

      {/* Facebook footer link */}
      <section className="border-b border-ink py-10" data-testid="sts-fb-footer">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink/60">Stay in the loop</p>
          <a
            href={FB_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sts-fb-link"
            className="mt-2 inline-flex items-center gap-3 font-alfa text-2xl uppercase leading-tight text-ink underline decoration-2 underline-offset-4 hover:text-ink/70 md:text-3xl"
          >
            <Facebook size={28} /> Follow the Small Town Showdown page →
          </a>
        </div>
      </section>
    </div>
  );
}
